import type { CurrencyPrimitive } from "@/api/cashier";
import { sanitizeNumberInput } from "@/utils/helpers";
import Decimal from "decimal.js";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export type AmountValidationError = "format" | "min" | "max";

export type AmountValidationResult =
  | { status: "empty" }
  | { status: "in-progress"; sanitized: string; decimal: Decimal }
  | {
      status: "invalid";
      sanitized: string;
      error: AmountValidationError;
      message: string;
    }
  | { status: "valid"; sanitized: string; decimal: Decimal };

type Options = {
  currency: CurrencyPrimitive;
  locale: string;
  min?: Decimal;
  max?: Decimal;
  /** Max decimal places allowed. Defaults to currency.decimals. */
  maxDecimalPlaces?: number;
  /** i18n key namespace for error messages. */
  errorKeys?: {
    invalid: string;
    min: string;
    max: string;
    /** Optional: shown when max is unbounded but value is non-zero. */
    alreadyCleared?: string;
  };
};

/*
  t("withdrawals.methods.amount_input.error_format")
  t("withdrawals.methods.amount_input.error_min")
  t("withdrawals.methods.amount_input.error_max")
  t("withdrawals.methods.amount_input.already_cleared")
*/
const DEFAULT_WTH_KEYS = {
  invalid: "withdrawals.methods.amount_input.error_format",
  min: "withdrawals.methods.amount_input.error_min",
  max: "withdrawals.methods.amount_input.error_max",
  alreadyCleared: "withdrawals.methods.amount_input.already_cleared",
};

export const useAmountValidation = ({
  currency,
  locale,
  min,
  max,
  maxDecimalPlaces,
  errorKeys = DEFAULT_WTH_KEYS,
}: Options) => {
  const { t } = useTranslation();
  const effectiveMin = min ?? new Decimal(0);
  const effectiveMax = max ?? new Decimal(Number.MAX_SAFE_INTEGER);
  const decimalCap = maxDecimalPlaces ?? currency?.decimals ?? 2;

  return useCallback(
    (raw: string): AmountValidationResult => {
      if (!raw?.length) return { status: "empty" };

      const { sanitized, numeric } = sanitizeNumberInput(raw, currency, locale);
      if (!sanitized) {
        return {
          status: "invalid",
          sanitized: "",
          error: "format",
          message: t(errorKeys.invalid),
        };
      }

      let decimal: Decimal;
      try {
        decimal = new Decimal(numeric);
      } catch {
        return {
          status: "invalid",
          sanitized,
          error: "format",
          message: t(errorKeys.invalid),
        };
      }

      if (
        errorKeys.alreadyCleared &&
        effectiveMax.eq(Number.MAX_SAFE_INTEGER) &&
        !decimal.isZero()
      ) {
        return {
          status: "invalid",
          sanitized,
          error: "max",
          message: t(errorKeys.alreadyCleared),
        };
      }

      if (decimal.isNaN() || (decimal.decimalPlaces() ?? 0) > decimalCap) {
        return {
          status: "invalid",
          sanitized,
          error: "format",
          message: t(errorKeys.invalid),
        };
      }

      if (decimal.gt(effectiveMax)) {
        return {
          status: "invalid",
          sanitized,
          error: "max",
          message: t(errorKeys.max, {
            max: effectiveMax.toNumber().toLocaleString(locale, {
              minimumFractionDigits: 0,
              maximumFractionDigits: currency.decimals,
              useGrouping: true,
            }),
          }),
        };
      }

      if (decimal.lt(effectiveMin)) {
        return {
          status: "invalid",
          sanitized,
          error: "min",
          message: t(errorKeys.min, {
            min: effectiveMin.toNumber().toLocaleString(locale, {
              minimumFractionDigits: 0,
              maximumFractionDigits: currency.decimals,
              useGrouping: true,
            }),
          }),
        };
      }

      const trimmed = sanitized.trim();
      const sepIdx = trimmed.indexOf(".");
      const decPart = sepIdx === -1 ? "" : trimmed.slice(sepIdx + 1);
      // In-progress while typing decimals: trailing dot OR trailing zeros
      // (e.g. "0.", "0.0", "1.10") — reformatting would drop precision.
      if (
        trimmed.endsWith(".") ||
        (decPart.length > 0 && decPart.endsWith("0"))
      ) {
        return { status: "in-progress", decimal, sanitized };
      }

      return { status: "valid", sanitized, decimal };
    },
    [currency, effectiveMin, effectiveMax, decimalCap, errorKeys, t, locale]
  );
};
